import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

/**
 * TooltipTrigger with automatic native tooltip suppression.
 *
 * Forces `title=""` so the browser never shows its own tooltip alongside
 * the Radix custom tooltip on truncated text.
 *
 * En mode `asChild`, Radix Slot ne propage PAS le `title=""` posé sur le
 * Trigger vers l'enfant. On clone donc explicitement l'enfant pour lui
 * imposer `title=""` (sauf si l'enfant a déjà un title non vide volontaire).
 * Sans ça, certains navigateurs (Chromium sur macOS notamment) affichent
 * un tooltip natif gris en plus du tooltip Radix dark — bug visuel.
 */
const TooltipTrigger = React.forwardRef<
    React.ComponentRef<typeof TooltipPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ title: _title, children, asChild, ...props }, ref) => {
    let injected = children
    if (asChild && React.isValidElement(children)) {
        const childProps = (children.props as { title?: string }) ?? {}
        // Si l'enfant a déjà un title=… non vide volontaire, on respecte.
        // Sinon on impose title="" pour bloquer le tooltip natif.
        if (!childProps.title) {
            injected = React.cloneElement(
                children as React.ReactElement<{ title?: string }>,
                { title: '' },
            )
        }
    }
    return (
        <TooltipPrimitive.Trigger ref={ref} title="" asChild={asChild} {...props}>
            {injected}
        </TooltipPrimitive.Trigger>
    )
})
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName

const TooltipContent = React.forwardRef<
    React.ComponentRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                "z-50 overflow-hidden rounded-md bg-[hsl(var(--anthracite))] px-3 py-1.5 text-xs text-white dark:text-gray-900",
                "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className
            )}
            {...props}
        />
    </TooltipPrimitive.Portal>
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
